/**
 * Google Analytics Tagging Utility
 * 
 * Provides functions to track events, page views, and user interactions
 * Uses dataLayer API compatible with both GTM and GA
 */

/**
 * Initialize GA event tracking
 * Call this after GA is loaded
 */
export const initializeGAEvents = () => {
  if (typeof window === 'undefined') return;
  
  // Ensure dataLayer exists
  window.dataLayer = window.dataLayer || [];
  
  // Define gtag function if not already defined
  if (!window.gtag) {
    window.gtag = function(...args) {
      window.dataLayer.push(args);
    };
  }
  
  console.log('✅ GA Events initialized');
};

/**
 * Track a custom event
 * @param {string} eventName - Event name (e.g., 'purchase', 'add_to_cart')
 * @param {object} eventData - Event data (key-value pairs)
 */
export const trackEvent = (eventName, eventData = {}) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('⚠️  GA not initialized, skipping event:', eventName);
    return;
  }
  
  try {
    window.gtag('event', eventName, {
      ...eventData,
      timestamp: new Date().toISOString()
    });
    console.log('📊 GA Event tracked:', eventName, eventData);
  } catch (error) {
    console.error('❌ Error tracking GA event:', error.message);
  }
};

/**
 * Track page view
 * @param {string} pagePath - Page path (e.g., '/cart', '/checkout')
 * @param {string} pageTitle - Page title
 */
export const trackPageView = (pagePath, pageTitle) => {
  trackEvent('page_view', {
    'page_path': pagePath,
    'page_title': pageTitle
  });
};

/**
 * Track user authentication
 * @param {string} method - Auth method (e.g., 'MSAL', 'SSO', 'password')
 * @param {boolean} success - Whether auth was successful
 */
export const trackAuthentication = (method, success) => {
  trackEvent('user_authentication', {
    'auth_method': method,
    'auth_success': success
  });
};

/**
 * Track cart operations
 * @param {string} action - Action (e.g., 'add', 'remove', 'update', 'view')
 * @param {object} cartData - Cart data (itemCount, totalValue, etc.)
 */
export const trackCartOperation = (action, cartData = {}) => {
  trackEvent('cart_operation', {
    'cart_action': action,
    ...cartData
  });
};

/**
 * Track product view
 * @param {string} productId - Product ID
 * @param {string} productName - Product name
 * @param {object} productData - Additional product data
 */
export const trackProductView = (productId, productName, productData = {}) => {
  trackEvent('product_view', {
    'product_id': productId,
    'product_name': productName,
    ...productData
  });
};

/**
 * Track search
 * @param {string} searchTerm - Search term
 * @param {object} searchData - Additional search data
 */
export const trackSearch = (searchTerm, searchData = {}) => {
  trackEvent('search', {
    'search_term': searchTerm,
    ...searchData
  });
};

/**
 * Track error
 * @param {string} errorName - Error name/type
 * @param {string} errorMessage - Error message
 */
export const trackError = (errorName, errorMessage) => {
  trackEvent('error', {
    'error_name': errorName,
    'error_message': errorMessage
  });
};

/**
 * Track token refresh
 * @param {string} status - Status (e.g., 'success', 'failure', 'retry')
 * @param {object} tokenData - Token data
 */
export const trackTokenRefresh = (status, tokenData = {}) => {
  trackEvent('token_refresh', {
    'refresh_status': status,
    ...tokenData
  });
};

/**
 * Set user properties
 * @param {object} userProperties - User properties (userId, userSegment, etc.)
 */
export const setUserProperties = (userProperties = {}) => {
  if (typeof window === 'undefined' || !window.gtag) {
    console.warn('⚠️  GA not initialized, skipping user properties');
    return;
  }
  
  try {
    window.gtag('set', userProperties);
    console.log('👤 GA User properties set:', userProperties);
  } catch (error) {
    console.error('❌ Error setting GA user properties:', error.message);
  }
};

/**
 * Track timing (for performance metrics)
 * @param {string} category - Timing category (e.g., 'page_load', 'api_request')
 * @param {string} timingName - Timing name
 * @param {number} milliseconds - Duration in milliseconds
 */
export const trackTiming = (category, timingName, milliseconds) => {
  trackEvent('timing', {
    'timing_category': category,
    'timing_name': timingName,
    'timing_value': milliseconds
  });
};

/**
 * Track purchase/order
 * @param {object} purchaseData - Purchase data (orderId, value, currency, items, etc.)
 */
export const trackPurchase = (purchaseData = {}) => {
  trackEvent('purchase', {
    ...purchaseData,
    'transaction_id': purchaseData.orderId || purchaseData.transactionId,
    'value': purchaseData.value || purchaseData.total,
    'currency': purchaseData.currency || 'USD'
  });
};

/**
 * Track form submission
 * @param {string} formName - Form name
 * @param {object} formData - Form data (fields, errors, etc.)
 */
export const trackFormSubmission = (formName, formData = {}) => {
  trackEvent('form_submission', {
    'form_name': formName,
    ...formData
  });
};

/**
 * Get current dataLayer (for debugging)
 * @returns {Array} Current dataLayer
 */
export const getDataLayer = () => {
  if (typeof window === 'undefined') return [];
  return window.dataLayer || [];
};

/**
 * Check if GA is available
 * @returns {boolean} True if GA is loaded
 */
export const isGAAvailable = () => {
  if (typeof window === 'undefined') return false;
  return !!(window.gtag && window.dataLayer);
};

export default {
  initializeGAEvents,
  trackEvent,
  trackPageView,
  trackAuthentication,
  trackCartOperation,
  trackProductView,
  trackSearch,
  trackError,
  trackTokenRefresh,
  setUserProperties,
  trackTiming,
  trackPurchase,
  trackFormSubmission,
  getDataLayer,
  isGAAvailable
};
